import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Bell, X, Shield, CheckCircle, WarningCircle, XCircle, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/hooks/useAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const mins = Math.floor((now - d) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function VerdictIcon({ verdict }) {
  if (verdict === "compliant") return <CheckCircle size={14} weight="fill" className="text-green-500 shrink-0" />;
  if (verdict === "borderline") return <WarningCircle size={14} weight="fill" className="text-amber-500 shrink-0" />;
  return <XCircle size={14} weight="fill" className="text-red-500 shrink-0" />;
}

const VERDICT_LABEL = {
  compliant: "Compliant", borderline: "Borderline", non_compliant: "Non-Compliant",
  pass: "Pass", fail: "Fail", unknown: "Unknown", unavailable: "Unavailable",
};
const VERDICT_COLOR = {
  compliant: "text-green-500", borderline: "text-amber-500", non_compliant: "text-red-500",
  pass: "text-green-500", fail: "text-red-500", unknown: "text-zinc-400", unavailable: "text-zinc-400",
};

export default function NotificationCenter({ onUpgrade }) {
  const { user, isPremium } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("compliance");
  const panelRef = useRef(null);

  // Compliance notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [premiumRequired, setPremiumRequired] = useState(false);

  // Portfolio alerts state
  const [portfolioAlerts, setPortfolioAlerts] = useState([]);
  const [portfolioChecked, setPortfolioChecked] = useState(false);
  const [portfolioLoading, setPortfolioLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
      setPremiumRequired(res.data.premium_required || false);
    } catch { /* */ }
  }, [user]);

  const fetchPortfolioAlerts = useCallback(async () => {
    if (!user) return;
    setPortfolioLoading(true);
    try {
      const res = await axios.get(`${API}/portfolio/alerts`, { withCredentials: true });
      setPortfolioAlerts(res.data.alerts || []);
      setPortfolioChecked(true);
    } catch { /* */ } finally {
      setPortfolioLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    fetchPortfolioAlerts();
    const i1 = setInterval(fetchNotifications, 60000);
    const i2 = setInterval(fetchPortfolioAlerts, 600000);
    return () => { clearInterval(i1); clearInterval(i2); };
  }, [fetchNotifications, fetchPortfolioAlerts]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const totalBadge = unreadCount + portfolioAlerts.length;

  const handleOpen = async () => {
    const opening = !open;
    setOpen(opening);
    if (opening) {
      // Default to tab with unread items
      if (portfolioAlerts.length > 0 && unreadCount === 0) setTab("portfolio");
      else setTab("compliance");
      // Mark compliance as read
      if (unreadCount > 0 && isPremium) {
        try {
          await axios.post(`${API}/notifications/read`, {}, { withCredentials: true });
          setUnreadCount(0);
          setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        } catch { /* */ }
      }
    }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={panelRef} data-testid="notification-center">
      <button
        onClick={handleOpen}
        className="relative w-9 h-9 flex items-center justify-center border border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 transition-all duration-[180ms] ease-in-out hover:bg-zinc-50 dark:hover:bg-zinc-800/60 hover:border-zinc-300 dark:hover:border-zinc-600 hover:text-zinc-700 dark:hover:text-zinc-200 hover:scale-105"
        data-testid="notification-bell"
        title="Notifications"
      >
        <Bell size={14} weight={totalBadge > 0 ? "fill" : "bold"} />
        {totalBadge > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[8px] font-mono font-bold flex items-center justify-center rounded-full" data-testid="notification-badge">
            {totalBadge > 9 ? "9+" : totalBadge}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 shadow-xl z-50" data-testid="notification-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 dark:border-zinc-800">
            <h3 className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500">
              Notifications
            </h3>
            <button onClick={() => setOpen(false)} className="p-1 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors">
              <X size={14} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-zinc-100 dark:border-zinc-800">
            <button
              onClick={() => setTab("portfolio")}
              className={`flex-1 py-2.5 text-[10px] font-mono uppercase tracking-wider text-center transition-colors border-b-2 ${
                tab === "portfolio"
                  ? "border-zinc-950 dark:border-zinc-100 text-zinc-950 dark:text-zinc-100"
                  : "border-transparent text-zinc-400 dark:text-zinc-600 hover:text-zinc-600 dark:hover:text-zinc-400"
              }`}
              data-testid="tab-portfolio-alerts"
            >
              Portfolio Alerts
              {portfolioAlerts.length > 0 && (
                <span className="ml-1.5 inline-flex w-4 h-4 items-center justify-center rounded-full bg-amber-500/15 text-amber-500 text-[8px] font-bold">
                  {portfolioAlerts.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setTab("compliance")}
              className={`flex-1 py-2.5 text-[10px] font-mono uppercase tracking-wider text-center transition-colors border-b-2 ${
                tab === "compliance"
                  ? "border-zinc-950 dark:border-zinc-100 text-zinc-950 dark:text-zinc-100"
                  : "border-transparent text-zinc-400 dark:text-zinc-600 hover:text-zinc-600 dark:hover:text-zinc-400"
              }`}
              data-testid="tab-compliance-alerts"
            >
              Compliance Alerts
              {unreadCount > 0 && (
                <span className="ml-1.5 inline-flex w-4 h-4 items-center justify-center rounded-full bg-red-500/15 text-red-500 text-[8px] font-bold">
                  {unreadCount}
                </span>
              )}
            </button>
          </div>

          {/* Content */}
          <div className="max-h-80 overflow-y-auto">
            {/* ── Portfolio Alerts Tab ── */}
            {tab === "portfolio" && (
              <>
                {portfolioLoading && !portfolioChecked ? (
                  <div className="px-4 py-6 text-center">
                    <div className="inline-block w-4 h-4 border-2 border-zinc-200 dark:border-zinc-700 border-t-zinc-950 dark:border-t-zinc-100 rounded-full animate-spin" />
                    <p className="text-[11px] font-mono text-zinc-400 mt-2">Checking holdings...</p>
                  </div>
                ) : portfolioAlerts.length === 0 ? (
                  <div className="px-4 py-6 text-center">
                    <Bell size={20} weight="thin" className="mx-auto text-zinc-300 dark:text-zinc-700 mb-2" />
                    <p className="text-[11px] font-mono text-zinc-400 dark:text-zinc-500">No portfolio alerts</p>
                    <p className="text-[10px] font-mono text-zinc-300 dark:text-zinc-600 mt-1">
                      We'll notify you when holdings change compliance status.
                    </p>
                  </div>
                ) : (
                  portfolioAlerts.map((alert) => (
                    <div
                      key={alert.id}
                      className="px-4 py-3 border-b border-zinc-50 dark:border-zinc-800/50 last:border-0 hover:bg-zinc-50 dark:hover:bg-zinc-900/40 transition-colors"
                      data-testid={`alert-${alert.ticker}`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <WarningCircle size={14} weight="fill" className="text-amber-500 shrink-0" />
                        <span className="text-xs font-mono font-medium text-zinc-950 dark:text-zinc-100">{alert.ticker}</span>
                        <span className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate">{alert.company_name}</span>
                      </div>
                      {alert.changes.map((change, i) => (
                        <div key={i} className="flex items-center gap-1.5 ml-5 text-[10px] font-mono">
                          <span className="text-zinc-400">{change.field}:</span>
                          <span className={VERDICT_COLOR[change.from] || "text-zinc-400"}>{VERDICT_LABEL[change.from] || change.from}</span>
                          <ArrowRight size={8} className="text-zinc-400" />
                          <span className={VERDICT_COLOR[change.to] || "text-zinc-400"}>{VERDICT_LABEL[change.to] || change.to}</span>
                        </div>
                      ))}
                    </div>
                  ))
                )}
                {portfolioChecked && (
                  <div className="px-4 py-2 border-t border-zinc-100 dark:border-zinc-800">
                    <button
                      onClick={fetchPortfolioAlerts}
                      disabled={portfolioLoading}
                      className="text-[10px] font-mono text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
                      data-testid="refresh-portfolio-alerts-btn"
                    >
                      {portfolioLoading ? "Checking..." : "Refresh alerts"}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* ── Compliance Alerts Tab ── */}
            {tab === "compliance" && (
              <>
                {premiumRequired ? (
                  <div className="p-6 text-center">
                    <Shield size={28} weight="duotone" className="text-emerald-500 mx-auto mb-3" />
                    <p className="text-sm text-zinc-700 dark:text-zinc-300 mb-1">Compliance Alerts</p>
                    <p className="text-[11px] font-mono text-zinc-400 dark:text-zinc-500 mb-3">
                      Never miss a compliance change. Get notified when your holdings change status.
                    </p>
                    <button
                      onClick={() => { setOpen(false); onUpgrade("Unlock real-time compliance alerts to protect your portfolio."); }}
                      className="px-4 py-2 text-[10px] font-mono uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
                      data-testid="alerts-upgrade-btn"
                    >
                      Unlock Alerts
                    </button>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="p-6 text-center">
                    <Bell size={24} weight="thin" className="text-zinc-300 dark:text-zinc-700 mx-auto mb-2" />
                    <p className="text-xs font-mono text-zinc-400 dark:text-zinc-500">No compliance alerts yet</p>
                    <p className="text-[10px] font-mono text-zinc-300 dark:text-zinc-600 mt-1">
                      You'll be notified when compliance status changes for your holdings.
                    </p>
                  </div>
                ) : (
                  notifications.map((n) => (
                    <div
                      key={n.id}
                      className={`px-4 py-3 border-b border-zinc-50 dark:border-zinc-800/50 transition-colors ${
                        !n.read ? "bg-emerald-50/30 dark:bg-emerald-950/10" : ""
                      }`}
                      data-testid={`notification-${n.id}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <VerdictIcon verdict={n.current_verdict} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-zinc-800 dark:text-zinc-200 font-medium">{n.title}</p>
                          <p className="text-[11px] font-mono text-zinc-500 dark:text-zinc-400 mt-0.5 leading-relaxed">{n.message}</p>
                          <p className="text-[9px] font-mono text-zinc-400 dark:text-zinc-600 mt-1">{timeAgo(n.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
